import React, { useContext, ComponentType } from 'react';
import { GroupBase, OptionProps } from 'react-select';
import { SelectOption } from '../MultiFilterDropdown';
import { MultiDropdownItem } from 'v2/models';
import { CheckboxDisplay } from 'v2/common/CheckBox/Checkbox';
import classnames from 'classnames';
import styles from './OptionComponent.module.scss';
import { CurrentStateFilterContext } from 'v2/context/currentStateFilterContext';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ReactComponent as Indication } from 'v2/icons/indication.svg';

interface ISelectOption<T extends MultiDropdownItem> {
  label: string;
  value: T;
}

function DefaultLabel<T extends MultiDropdownItem>({
  label,
  value,
}: ISelectOption<T>) {
  const labelDisabled = value.isDisabled ? styles.disabledLabel : '';
  return (
    <div className={styles.labelWithIconContainer}>
      <span className={classnames(styles.defaultLabel, labelDisabled)}>
        {label}
      </span>
      {value.tooltip && (
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id={`tooltip-${label}`} className={styles.tooltip}>
              {value.tooltip}
            </Tooltip>
          }
        >
          <Indication className={styles.indicationIcon} data-testid="tooltip" />
        </OverlayTrigger>
      )}
    </div>
  );
}

interface IOptionComponentProps<T extends MultiDropdownItem>
  extends OptionProps<SelectOption<T>, true, GroupBase<SelectOption<T>>> {
  LabelComponent?: ComponentType<ISelectOption<T>>;
}

function OptionComponent<T extends MultiDropdownItem>({
  data: option,
  LabelComponent = DefaultLabel,
  ...rest
}: IOptionComponentProps<T>) {
  const { currentState } = useContext(CurrentStateFilterContext);
  const { value } = option;

  const elementIndex = currentState.list.findIndex(
    (element) => element.id === value.id
  );
  const checked = elementIndex !== -1;
  const inputTestId = `${rest.selectProps.id}-option-${value.id}_input`;
  return (
    <div className={classnames(styles.option)} {...rest.innerProps}>
      <CheckboxDisplay
        checked={checked}
        disabled={value.isDisabled}
        readOnly
        dataTestId={inputTestId}
      >
        <LabelComponent {...option} />
      </CheckboxDisplay>
    </div>
  );
}

export default OptionComponent;
